import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { setTimeZone } from "@/lib/format";
import { useAuth } from "./AuthContext";

const PrefsCtx = createContext(null);
const DEFAULT_HOTKEYS = { new_call: "F1", officer_initiated: "F2", search: "F3", entity_search: "F4", map: "F5", commit: "Ctrl+Enter", command_bar: "/" };

export function PrefsProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState({ theme: "dark", layout: {}, hotkeys: {} });
  const [applied, setApplied] = useState(false);
  const saveTimer = useRef(null);

  const settingsQ = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data, enabled: !!user });
  const prefsQ = useQuery({ queryKey: ["prefs", user?.id], queryFn: async () => (await api.get("/me/preferences")).data, enabled: !!user });

  useEffect(() => { if (prefsQ.data) { setPrefs({ theme: "dark", layout: {}, hotkeys: {}, ...prefsQ.data }); setApplied(true); } }, [prefsQ.data]);
  useEffect(() => { if (!user) setApplied(false); }, [user]);
  useEffect(() => { if (settingsQ.data?.timezone) setTimeZone(settingsQ.data.timezone); }, [settingsQ.data]);

  const theme = prefs.theme || settingsQ.data?.default_theme || "dark";
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const persist = useCallback((next) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { api.put("/me/preferences", next).catch(() => {}); }, 500);
  }, []);

  const setTheme = useCallback((t) => setPrefs((p) => { const n = { ...p, theme: t }; persist({ theme: t }); return n; }), [persist]);
  const saveLayout = useCallback((key, sizes) => setPrefs((p) => {
    const layout = { ...(p.layout || {}), [key]: sizes };
    persist({ layout });
    return { ...p, layout };
  }), [persist]);
  const setHotkey = useCallback((name, key) => setPrefs((p) => {
    const hotkeys = { ...(p.hotkeys || {}), [name]: key };
    persist({ hotkeys });
    return { ...p, hotkeys };
  }), [persist]);

  const hotkeys = useMemo(() => ({ ...DEFAULT_HOTKEYS, ...(settingsQ.data?.hotkeys || {}), ...(prefs.hotkeys || {}) }), [settingsQ.data, prefs.hotkeys]);

  const value = { prefs, theme, setTheme, saveLayout, setHotkey, hotkeys, settings: settingsQ.data || {}, loaded: applied };
  return <PrefsCtx.Provider value={value}>{children}</PrefsCtx.Provider>;
}

export const usePrefs = () => useContext(PrefsCtx);
